import type { ReactNode } from 'react';
import { t } from '../services/language-manager';
import { actionTypeLabel, isActionType, type ActionType } from '../services/action-types';
import { BilledIcon, BilledMark } from './BilledMark';

interface ActionTypePickerProps {
  /** Input id prefix; the first card's radio gets exactly this id so a failed save can focus it. */
  id: string;
  /** Types the org may create, as for the old dropdown; cards for other types are not shown. */
  allowedTypes: string[];
  /** Types whose use is charged to the org (GET /api/org-actions `billedTypes`); they carry the billed mark. */
  billedTypes: string[];
  value: string;
  onChange: (type: string) => void;
  invalid: boolean;
  /** Save feedback element to point at while `invalid`. */
  describedBy?: string;
}

interface TypeGroup {
  titleKey: string;
  hintKey: string;
  types: ActionType[];
}

/** Grouped by what the action does; order within a group is the reading order on the page. */
const GROUPS: TypeGroup[] = [
  {
    titleKey: 'settings-actions-type-group-reply',
    hintKey: 'settings-actions-type-group-reply-hint',
    types: ['AI_REPLY'],
  },
  {
    titleKey: 'settings-actions-type-group-info',
    hintKey: 'settings-actions-type-group-info-hint',
    types: ['CONVERSATION_INFO'],
  },
  {
    titleKey: 'settings-actions-type-group-system',
    hintKey: 'settings-actions-type-group-system-hint',
    types: ['CREATE_ORDER', 'CREATE_RETURN', 'SEND_FILES'],
  },
];

const CARD_DESCRIPTION_KEYS: Record<ActionType, string> = {
  AI_REPLY: 'settings-actions-type-card-ai-reply',
  CREATE_ORDER: 'settings-actions-type-card-create-order',
  CREATE_RETURN: 'settings-actions-type-card-create-return',
  SEND_FILES: 'settings-actions-type-card-send-files',
  CONVERSATION_INFO: 'settings-actions-type-card-conversation-info',
};

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function TypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'AI_REPLY':
      return (
        <Icon>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M8 9h8M8 13h5" />
        </Icon>
      );
    case 'CREATE_ORDER':
      return (
        <Icon>
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </Icon>
      );
    case 'CREATE_RETURN':
      return (
        <Icon>
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </Icon>
      );
    case 'SEND_FILES':
      return (
        <Icon>
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </Icon>
      );
    case 'CONVERSATION_INFO':
      return (
        <Icon>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </Icon>
      );
    default:
      return (
        <Icon>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </Icon>
      );
  }
}

/**
 * New-action type choice as cards grouped by purpose. One native radio group
 * across all groups (shared `name`), so Tab enters it once and the arrow keys
 * move between every card. A type the org does not offer has no card; an
 * offered type this wizard does not know yet goes under "Other actions".
 * Billed types carry a mark; one line under the legend says the others are free.
 */
export function ActionTypePicker({ id, allowedTypes, billedTypes, value, onChange, invalid, describedBy }: ActionTypePickerProps) {
  const groups = GROUPS
    .map((g) => ({ ...g, types: g.types.filter((type) => allowedTypes.includes(type)) as string[] }))
    .filter((g) => g.types.length > 0);
  const unknown = allowedTypes.filter((type) => !isActionType(type));
  if (unknown.length > 0) {
    groups.push({ titleKey: 'settings-actions-type-group-other', hintKey: '', types: unknown });
  }
  const firstType = groups[0]?.types[0];
  const offeredBilled = allowedTypes.filter((type) => billedTypes.includes(type));
  const someFree = offeredBilled.length < allowedTypes.length;

  return (
    <fieldset className={`type-picker${invalid ? ' type-picker--invalid' : ''}`}>
      <legend className="form-label">{t('settings-actions-type-label')}</legend>
      {offeredBilled.length > 0 && (
        <p className="form-hint type-picker__billed-legend">
          <BilledIcon />
          <span>
            {t('settings-actions-billed-legend')}
            {someFree && ` ${t('settings-actions-billed-legend-free')}`}
          </span>
        </p>
      )}
      {groups.map((group) => {
        const groupId = `${id}-group-${group.titleKey}`;
        return (
          <div className="type-picker__group" role="group" aria-labelledby={groupId} key={group.titleKey}>
            <p className="type-picker__group-title" id={groupId}>{t(group.titleKey)}</p>
            {group.hintKey && <p className="form-hint type-picker__group-hint">{t(group.hintKey)}</p>}
            <div className="type-picker__cards">
              {group.types.map((type) => {
                const inputId = type === firstType ? id : `${id}-${type}`;
                const nameId = `${inputId}-name`;
                const descId = `${inputId}-desc`;
                const billedId = `${inputId}-billed`;
                const description = isActionType(type) ? t(CARD_DESCRIPTION_KEYS[type]) : '';
                const label = actionTypeLabel(type);
                const billed = billedTypes.includes(type);
                return (
                  <div className="type-card" key={type}>
                    <input
                      id={inputId}
                      className="type-card__input"
                      type="radio"
                      name={id}
                      value={type}
                      checked={value === type}
                      onChange={() => onChange(type)}
                      aria-labelledby={nameId}
                      aria-describedby={[description ? descId : '', billed ? billedId : '', invalid && describedBy ? describedBy : '']
                        .filter(Boolean).join(' ') || undefined}
                      aria-invalid={invalid || undefined}
                    />
                    <label className="type-card__body" htmlFor={inputId}>
                      <span className="type-card__icon"><TypeIcon type={type} /></span>
                      <span className="type-card__text">
                        <span className="type-card__name-row">
                          <span className="type-card__name" id={nameId}>{label}</span>
                          {billed && <BilledMark tooltipId={billedId} />}
                        </span>
                        {description && <span className="type-card__desc" id={descId}>{description}</span>}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </fieldset>
  );
}
